import { Box } from '#/components/Box/Box';
import { LegacySecondaryButton } from '#/components/LegacyButton/LegacySecondaryButton';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { LoadingSection } from '#/components/LoadingSection/LoadingSection';
import { RichTextHeadline } from '#/components/Text/RichTextHeadline';
import { Underline } from '#/components/Text/Underline';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiRecentSalariesBlockData } from '#/modules/cms/api/types/blocks/CmsApiRecentSalariesBlock';
import { useSalaryApiFetchRecentSubmissions } from '#/modules/salary/api/salaryApiFetchRecentSubmissions';
import { colors } from '#/theme/theme';
import { trackAnchorClicked } from '#/utils/analytics/track/trackAnchorClicked';

import {
  CmsRecentSalariesBlockACycle,
  CmsRecentSalariesBlockHeading,
  CmsRecentSalariesBlockHolder,
  CmsRecentSalariesBlockTableBody,
  CmsRecentSalariesBlockTableBodyHolder,
  CmsRecentSalariesBlockTableCell,
  CmsRecentSalariesBlockTableContainer,
  CmsRecentSalariesBlockTableHeading,
  CmsRecentSalariesBlockWrapper,
} from './CmsRecentSalariesBlock.styled';

type Props = {
  data: CmsApiRecentSalariesBlockData;
  blockIndex: number;
  pageUpdated: string;
};

export function CmsRecentSalariesBlock({
  data: blockData,
  blockIndex,
  pageUpdated,
}: Props) {
  const { i18n, locale } = useI18n();

  const [recentSubmissions] = useSalaryApiFetchRecentSubmissions();

  const tableHeading = i18n('{{s}}Recent Salary Submissions{{/s}}', {
    s: <Underline />,
  });

  const data = recentSubmissions
    ? recentSubmissions
        .concat(recentSubmissions.slice(0, 4))
        .map((submission) => [
          submission.title,
          submission.salary.toLocaleString(locale, {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
          }),
          submission.location,
        ])
    : [];

  return (
    <CmsRecentSalariesBlockHolder>
      <CmsRecentSalariesBlockWrapper>
        <CmsRecentSalariesBlockHeading>
          <RichTextHeadline>{blockData.headline}</RichTextHeadline>

          <LegacySecondaryButton
            component={StyledNativeLinkWithRef}
            href={blockData.buttonUrl}
            $reverse
            onClick={(event) =>
              trackAnchorClicked(event, 'CMS Block', {
                block_position: blockIndex,
                block_type: 'Recent Salaries',
                cta_type: 'Button',
                button_text: blockData.buttonText,
                page_updated: pageUpdated,
              })
            }
          >
            {blockData.buttonText}
          </LegacySecondaryButton>
        </CmsRecentSalariesBlockHeading>

        <CmsRecentSalariesBlockTableContainer>
          <CmsRecentSalariesBlockTableHeading>
            {tableHeading}
          </CmsRecentSalariesBlockTableHeading>
          {!recentSubmissions ? (
            <LoadingSection />
          ) : (
            <>
              <Box
                display="flex"
                fontWeight={600}
                backgroundColor={colors.backgroundGrey}
              >
                <CmsRecentSalariesBlockTableCell
                  style={{ width: '65%', color: colors.lightContentGrey }}
                >
                  {i18n('Position')}
                </CmsRecentSalariesBlockTableCell>
                <CmsRecentSalariesBlockTableCell
                  style={{ width: '15%', color: colors.lightContentGrey }}
                >
                  {i18n('Salary')}
                </CmsRecentSalariesBlockTableCell>
                <CmsRecentSalariesBlockTableCell
                  style={{ width: '20%', color: colors.lightContentGrey }}
                >
                  {i18n('Location')}
                </CmsRecentSalariesBlockTableCell>
              </Box>

              <CmsRecentSalariesBlockTableBodyHolder>
                <CmsRecentSalariesBlockTableBody
                  $animationName={CmsRecentSalariesBlockACycle(data)}
                  $itemCount={data.length}
                >
                  {data.map((item, i) => (
                    <Box key={[...item, i].join()} display="flex">
                      <CmsRecentSalariesBlockTableCell style={{ width: '65%' }}>
                        {item[0]}
                      </CmsRecentSalariesBlockTableCell>
                      <CmsRecentSalariesBlockTableCell style={{ width: '15%' }}>
                        {item[1]}
                      </CmsRecentSalariesBlockTableCell>
                      <CmsRecentSalariesBlockTableCell style={{ width: '20%' }}>
                        {item[2]}
                      </CmsRecentSalariesBlockTableCell>
                    </Box>
                  ))}
                </CmsRecentSalariesBlockTableBody>
              </CmsRecentSalariesBlockTableBodyHolder>
            </>
          )}
        </CmsRecentSalariesBlockTableContainer>
      </CmsRecentSalariesBlockWrapper>
    </CmsRecentSalariesBlockHolder>
  );
}
